import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { BillingExclusion, BillingExclusionSaveRequest, GUID } from '@ndvr/billing-api';
import { Account } from '@ndvr/portfolio-api';

import { BillingApi } from 'API';
import { Toast } from 'widgets';

const BillingExclusionQueryKeys = {
    all: ['billingExclusions'] as const,
    byAccounts: (accountGUIDs: GUID<Account>[]) => ['billingExclusions', 'accounts', [...accountGUIDs].sort()] as const,
    byAccount: (accountGUID: GUID<Account>) => ['billingExclusions', 'account', accountGUID] as const,
    // Top-level key so the save invalidation (which matches the 'billingExclusions'
    // prefix) can't reach this static, never-changing reference data.
    cashEquivalents: ['cashEquivalentShareClassIds'] as const,
} as const;

// Batch-loads exclusions keyed by accountGUID. The endpoint omits accounts without
// exclusions, so a missing key means "no exclusions".
export const useBillingExclusions = (accountGUIDs: GUID<Account>[]) =>
    useQuery({
        queryKey: BillingExclusionQueryKeys.byAccounts(accountGUIDs),
        enabled: accountGUIDs.length > 0,
        queryFn: async (): Promise<Map<GUID<Account>, BillingExclusion>> => {
            const [response] = await BillingApi().getBillingExclusions(accountGUIDs);
            return new Map((response ?? []).map((exclusion) => [exclusion.accountGUID, exclusion]));
        },
    });

// Loads one account's exclusion; the edit dialog seeds its form from this.
export const useBillingExclusion = (accountGUID: GUID<Account> | undefined) =>
    useQuery({
        queryKey: BillingExclusionQueryKeys.byAccount(accountGUID!),
        enabled: !!accountGUID,
        queryFn: async (): Promise<BillingExclusion> => {
            const [response] = await BillingApi().getBillingExclusion(accountGUID!);
            // Always returns an exclusion (empty when none); client types it nullable.
            return (
                response ?? {
                    accountGUID: accountGUID!,
                    excludedShareClassIds: [],
                    excludedCash: false,
                    populated: false,
                }
            );
        },
    });

// Persists an account's exclusion (saved independently of any billing group) and
// invalidates the exclusion queries so the list and dialog refresh.
export const useSetBillingExclusion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ accountGUID, request }: { accountGUID: GUID<Account>; request: BillingExclusionSaveRequest }) =>
            BillingApi({ hideError: true }).setBillingExclusion(accountGUID, request),
        onSuccess: () => {
            toast.success('Billing exclusions saved');
            queryClient.invalidateQueries({ queryKey: BillingExclusionQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to save billing exclusions'),
    });
};

// Share class ids treated as cash equivalents, shown as a hint by the cash toggle.
export const useCashEquivalentShareClasses = () =>
    useQuery({
        queryKey: BillingExclusionQueryKeys.cashEquivalents,
        queryFn: async (): Promise<string[]> => {
            const [response] = await BillingApi().getCashEquivalentShareClassIds();
            return response ?? [];
        },
        staleTime: Infinity,
    });
